import React from 'react';
import { PopoverProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import NotStartedOutlinedIcon from '@mui/icons-material/NotStartedOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditNoteIcon from '@mui/icons-material/EditNote';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CardStatusMenu, { CardStatusItem, CardStatusMenuAction } from '../common/CardStatusMenu';
import { STATUS_COLOR } from '../../constants/statusColors';

/**
 * ゲームカードのステータスメニュー。器は `common/CardStatusMenu`（アニメ・映画と共有）で、
 * このファイルはゲーム固有の「ステータスの集合」と「導線」を組み立てるだけ。
 */

/**
 * ステータスの意味色。
 *
 * **値は既存の GameCard / GameStatusButton の指定をそのまま踏襲している。**
 * 1 だけ `statusColors.ts`、2/3/5 はテーマトークン、4 は生 hex という混在で、
 * テーマトークン側はプロフィールのアクセント色（入れ子 ThemeProvider が primary/secondary を
 * 差し替える）に追従してしまう。これを `statusColors.ts` へ寄せるのは別作業なので、
 * ここでは見た目を変えないことを優先し、**解決を1箇所に閉じ込めるだけ**にしてある。
 */
/**
 * `fullKey` は完全な文言のロケールキー。`status.N` は**セグメント用の短い表記**で、
 * 5 だけ「ウィッシュ」と「ウィッシュリスト」で長さが違う。
 * 縦リストと読み上げには完全な方を出す（アニメ・映画も同じ作りにしてある）。
 */
const STATUS_DEFS: {
  code: number;
  icon: React.ReactElement;
  color: (theme: Theme) => string;
  fullKey: string;
}[] = [
  { code: 1, icon: <CheckCircleOutlineIcon />, color: () => STATUS_COLOR[1], fullKey: 'menu.played' },
  { code: 2, icon: <PlayCircleOutlineIcon />, color: (theme) => theme.palette.info.main, fullKey: 'menu.playing' },
  { code: 3, icon: <PauseCircleOutlineIcon />, color: (theme) => theme.palette.warning.main, fullKey: 'menu.paused' },
  { code: 4, icon: <NotStartedOutlinedIcon />, color: () => '#b99a71', fullKey: 'menu.backlog' },
  { code: 5, icon: <StarBorderIcon />, color: (theme) => theme.palette.secondary.main, fullKey: 'menu.wishlist' },
];

interface GameCardStatusMenuProps {
  open: boolean;
  /** 左クリックは要素、右クリックはカーソル位置の仮想アンカー要素が入る */
  anchorEl: PopoverProps['anchorEl'];
  onClose: () => void;
  gameTitle: string;
  status: number | null;
  onSelectStatus: (status: number) => void;
  variant?: 'segmented' | 'list';
  /** 以下は「その場面で使えるときだけ」渡す。undefined の項目はボタン自体を出さない */
  onEditStatus?: () => void;
  onEditMemo?: () => void;
  hasMemo?: boolean;
  onAddToList?: () => void;
  onAddToFolder?: () => void;
  onLaunchSteam?: () => void;
  onDelete?: () => void;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
}

const GameCardStatusMenu: React.FC<GameCardStatusMenuProps> = ({
  open,
  anchorEl,
  onClose,
  gameTitle,
  status,
  onSelectStatus,
  variant = 'segmented',
  onEditStatus,
  onEditMemo,
  hasMemo = false,
  onAddToList,
  onAddToFolder,
  onLaunchSteam,
  onDelete,
  anchorOrigin,
  transformOrigin,
}) => {
  const { t } = useTranslation('gameCard');

  const statusItems: CardStatusItem[] = STATUS_DEFS.map(({ code, icon, color, fullKey }) => ({
    code,
    icon,
    color,
    label: t(`status.${code}`),
    fullLabel: t(fullKey),
  }));

  const actions: CardStatusMenuAction[] = [
    ...(onEditStatus ? [{ icon: <EditNoteIcon />, label: t('menu.editStatus'), onClick: onEditStatus }] : []),
    ...(onEditMemo ? [{
      icon: <StickyNote2OutlinedIcon />,
      label: hasMemo ? t('menu.editMemo') : t('menu.createMemo'),
      onClick: onEditMemo,
    }] : []),
    ...(onAddToList ? [{ icon: <PlaylistAddIcon />, label: t('menu.addToList'), onClick: onAddToList }] : []),
    ...(onAddToFolder ? [{ icon: <CreateNewFolderOutlinedIcon />, label: t('menu.addToFolder'), onClick: onAddToFolder }] : []),
    ...(onLaunchSteam ? [{
      // 以前はこの行だけ青グラデの面を持っていた。項目の中で唯一の色面だったため
      // 優先度と無関係に視線を集めていたので、他の導線と同じ扱いに落としてある。
      // 実寸は枠(1rem=20px)より少し小さくする。ベタ塗りのロゴなので、
      // 線画の MUI アイコンと同寸にすると1つだけ重く見える。位置は枠側で中央に揃う。
      icon: (
        <img
          src="/icons/service/steam_black.svg"
          alt=""
          style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)', opacity: 0.85 }}
        />
      ),
      label: t('menu.launch'),
      onClick: onLaunchSteam,
    }] : []),
  ];

  return (
    <CardStatusMenu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title={gameTitle}
      statusItems={statusItems}
      status={status}
      onSelectStatus={onSelectStatus}
      variant={variant}
      actions={actions}
      onDelete={onDelete}
      deleteLabel={t('menu.delete')}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    />
  );
};

export default GameCardStatusMenu;
